import {
  IServiceRequest,
  TUseServiceRequestsOptions,
} from '@commenda/types/src/service-request/types';
import { AppType, SWRFetcher, useApp } from '@commenda/utils';
import { useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import { SERVICE_REQUESTS_SERVER_PAGE_LIMIT } from './constants';
import {
  buildPaginatedQueryString,
  IServiceRequestResponse,
} from './useServiceRequestsPaginated';

const maxPageSize = SERVICE_REQUESTS_SERVER_PAGE_LIMIT;

type InfiniteOptions = TUseServiceRequestsOptions & {
  pageSize?: number;
  enabled?: boolean;
  autoLoadAll?: boolean;
};

export const useServiceRequestsInfinite = (options?: InfiniteOptions) => {
  const {
    pageSize = maxPageSize,
    enabled = true,
    autoLoadAll = false,
    ...filters
  } = options ?? {};

  const { appType } = useApp();
  const isAgentApp = appType === AppType.AGENT;

  // Build a stable base URL for the FIRST page (no cursor)
  const base = buildPaginatedQueryString({
    ...filters,
    pageSize,
    enabled,
    isAgentApp,
  });

  // SWRInfinite key builder
  const getKey = (
    pageIndex: number,
    prevPage: IServiceRequestResponse | null
  ): string | null => {
    if (!enabled) return null;
    if (pageIndex === 0) return base; // first set of results no cursor
    if (!prevPage?.nextCursor) return null; // stop when there is no next page
    // not the first page, pass in the cursor from the previous page
    const next = buildPaginatedQueryString({
      ...filters,
      pageSize,
      enabled,
      isAgentApp,
      cursor: prevPage.nextCursor,
    });
    return next;
  };

  const { data, error, mutate, size, setSize, isLoading, isValidating } =
    useSWRInfinite<IServiceRequestResponse>(
      getKey,
      SWRFetcher(
        {
          method: 'GET',
        },
        undefined
      ),
      {
        onSuccess: (pages) => {
          if (!autoLoadAll) return;
          const last = pages[pages.length - 1];
          if (last?.nextCursor) {
            // queue the next page
            setSize((p) => p + 1);
          }
        },
        revalidateFirstPage: false,
      }
    );

  // Flatten items across pages
  const items = useMemo<IServiceRequest[]>(
    () => (Array.isArray(data) ? data.flatMap((p) => p?.items ?? []) : []),
    [data]
  );

  const lastPage = Array.isArray(data) ? data.at(-1) : undefined;
  const hasNextPage = !!lastPage?.nextCursor;

  const loadMore = () => setSize(size + 1);

  const reset = () => setSize(0);

  const firstPage = data?.[0];
  const meta = firstPage?.meta;

  return {
    data: items,
    pages: data ?? [],
    hasNextPage,
    loadMore,
    reset,
    error,
    mutate,
    size,
    isLoading: !!isLoading,
    isValidating: !!isValidating,
    meta,
  };
};
